/**
 * useUserGraphQL Hook
 *
 * Fetches a single user by global ID using GraphQL.
 *
 * Feature: 065-graphql-migration-cleanup
 */

import { gql, useQuery } from '@apollo/client';

const GET_USER = gql`
  query GetUser($id: ID!) {
    user(id: $id) {
      id
      globalId
      username
      email
      displayName
      slug
      emailVerified
      isTestUser
      isAdmin
      createdAt
      updatedAt
      lastLoginAt
      photoCount
      collectionCount
      invitedBy {
        id
        globalId
        username
        displayName
      }
    }
  }
`;

/**
 * Minimal user info for inviter display (Feature: 891-invite-attribution)
 */
export interface InviterInfo {
  id: string;
  globalId: string;
  username: string;
  displayName: string | null;
}

export interface UserDetail {
  id: string;
  globalId: string;
  username: string;
  email: string;
  displayName: string;
  slug: string;
  emailVerified: boolean;
  isTestUser: boolean;
  isAdmin: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
  photoCount?: number;
  collectionCount?: number;
  invitedBy?: InviterInfo | null; // Feature: 891-invite-attribution
}

// Type that matches UserDetailResponse for backward compatibility
export interface UserDetailResponse {
  user: UserDetail;
  stats: {
    photoCount: number;
    collectionCount: number;
    critiqueCount: number;
    lastActivityDate: string | null;
  };
}

interface GetUserData {
  user: UserDetail | null;
}

/**
 * Fetch a single user by global ID
 * Returns data in UserDetailResponse format for backward compatibility
 */
export function useUserGraphQL(userId: string | undefined) {
  const { data, loading, error, refetch } = useQuery<GetUserData>(GET_USER, {
    // CRITICAL: Always provide a placeholder ID when skipping to prevent Apollo Client
    // from validating required variables before checking skip.
    variables: { id: userId || 'skip-placeholder' },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  // Transform to UserDetailResponse format
  const userDetailResponse: UserDetailResponse | undefined = data?.user
    ? {
        user: data.user,
        stats: {
          photoCount: data.user.photoCount ?? 0,
          collectionCount: data.user.collectionCount ?? 0,
          critiqueCount: 0, // Not available in current query
          lastActivityDate: data.user.lastLoginAt,
        },
      }
    : undefined;

  return {
    data: userDetailResponse,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}
